import React, { memo, useCallback, useEffect, useState } from "react"
import { Alert, Appearance, ImageStyle, InteractionManager, TextStyle } from "react-native"
import { observer } from "mobx-react-lite"
import { Toast, View } from "native-base"
import _ from "lodash"
import numeral from "numeral"
import { AutoImage } from "../auto-image/auto-image"
import { Button } from "../button/button"
import { JalaIcon } from "../jala-icon/jala-icon"
import { Text } from "../text/text"
import { canBePushedCount, canBePushedPushAll, canBePushedSyncAll } from "../../database"
import { color } from "../../theme"
import { createAxios } from "../../utils/axios"
import { navigate } from "../../navigators"
import { translate } from "../../i18n"
import { useModelStoresStatus, useStores } from "../../models"
import JalaAnalytic from "../../utils/jala-analytic"
import { useFocusEffect, useIsFocused } from "@react-navigation/native"
import { IViewProps } from "native-base/lib/typescript/components/primitives/View"

const IMAGE: ImageStyle = {
  width: 48,
  height: 48,
  resizeMode: "contain",
}

const BTN_TEXT = (progress, errors, isSuccess): TextStyle => ({
  color: progress === null && errors.length ? color.palette.red600 : (
    (isSuccess && color.palette.green600) || color.primary
  ),
})

export interface OfflineModeProps extends IViewProps {
  /**
   * An optional style override useful for padding & margin.
   */
  colorScheme?: "light" | "dark",
  navigation?: boolean,
  onFinishPushAll?: () => void
}

/**
 * Describe your component here
 */

const img = require("./offline-illustration.png")


export const OfflineMode = memo(observer(function OfflineMode(props: OfflineModeProps) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    navigation = false,
    onFinishPushAll, ...restProps
  } = props

  const {
    appStore,
    appStore: { offlineData, progressOffline: progress, errorOffline: errors },
    userStore: { user }
  } = useStores()

  const [isSuccess, setSuccess] = useState<boolean>(false)
  const [collapse, setCollapse] = useState<boolean>(true)
  const [, setMounted] = useState<boolean>(false)
  const [pushOnFocus, setPushOnFocus] = useState<boolean>(false)
  const [isLoading, setLoading] = useState<boolean>(false)

  const isFocused = useIsFocused()
  const { modifying } = useModelStoresStatus()

  const getOfflineData = useCallback(() => {
    return canBePushedCount()
      .then(counts => {
        appStore.set("offlineData", counts)
        return counts;
      })
      .catch(() => {
        appStore.set("offlineData", 0)
        return 0;
      })
  }, [appStore])

  useFocusEffect(useCallback(() => {
    if (modifying)
      appStore.set("offlineData", 0)
    else
      InteractionManager.runAfterInteractions(getOfflineData)
  }, [appStore, getOfflineData, modifying]))

  const pushAll = useCallback(async () => {
    appStore.set("progressOffline", "0")
    setLoading(true)
    await appStore.checkNetworkConnection().catch().finally(() => setLoading(false))
    if (appStore.isOffline || !appStore.offlineData) {
      appStore.set("progressOffline", null)
      return
    }
    appStore.set("progressOffline", "0")
    appStore.set("errorOffline", [])

    JalaAnalytic.log("Mobile app: home - offline first - upload")

    const onPushProgressUpdate = (progress = null) => {
      appStore.set("progressOffline", (
        progress === null || progress === 100 ? null : numeral(progress).format("0,0")
      ))
    }

    const onError = (error = null) => {
      if (error) appStore.set("errorOffline", [...errors, error])
    }

    return canBePushedPushAll(false, onPushProgressUpdate, onError)
      .then(() => canBePushedCount().catch(() => 0))
      .then((counts) => setSuccess(!counts))
      .finally(() => {
        if (appStore.errorOffline.length) {
          const errors = appStore.errorOffline
          const error = (errors.find((e) => e.message) || errors[0])
          Alert.alert(
            translate("Errors"),
            `${error.message}\n${errors.length > 1 ? `...\nTotalError: ${errors.length}` : ""}`,
            [navigation ? {
              text: translate("profileScreen.support"),
              onPress: () => navigate("Home.Stack", "App.Stack", { screen: "Help.Help" }),
            } : {}, {
              text: _.upperFirst(translate("reset")),
              onPress: () => {
                createAxios().get("/ping").then(() => {
                  canBePushedSyncAll(onPushProgressUpdate, onError)
                    .finally(() => {
                      JalaAnalytic.log(
                        'Mobile app: offline mode - success push All Data',
                        { email: user?.email, phone: user?.phone }
                      );
                      setTimeout(() => {
                        appStore.set("errorOffline", [])
                        setSuccess(false)
                        getOfflineData().catch()
                      }, 2500)
                    })
                }).catch((e) => {
                  if (e.message && e.message === "Network Error" && !Toast.isActive(e.name))
                    Toast.show({ id: `offline-mode-${e.name}`, title: translate("offlineMode.resetRequireInternet") })
                  else if (e.message && !Toast.isActive(e.name))
                    Toast.show({ id: `offline-mode-${e.name}`, title: e.message })
                })
              },
            }, {
              text: "Ok",
            }].filter((b) => !_.isEmpty(b)),
          )
        }
        setTimeout(() => {
          appStore.set("errorOffline", [])
          setSuccess(false)
          getOfflineData()
            .catch()
            .finally(() => {
              onFinishPushAll && onFinishPushAll()
            })
        }, 2500)
      })
  }, [appStore, errors, getOfflineData, navigation, onFinishPushAll, user?.email, user?.phone])

  const modelsCount = offlineData !== null ? ` : ${numeral(offlineData).format('0,0')}` : '...';
  const buttonText = progress === null
    ? ((errors.length && translate("failed")) || (isSuccess && translate("success")) || translate("upload"))
    : (`${progress}%`)

  useFocusEffect(useCallback(() => {
    setMounted((prevState) => {
      if (prevState) setPushOnFocus(true)
      return true
    })
    return () => setPushOnFocus(false)
  }, []))

  useEffect(() => {
    if (pushOnFocus && offlineData) {
      setPushOnFocus(false)
      if (appStore.progressOffline !== null) return
      pushAll().catch()
    }
  }, [pushOnFocus, pushAll, appStore, offlineData])

  if (!isFocused || !offlineData || modifying) return null

  return (
    <View position="absolute" right={0} bottom={0} left={0}>
      <View
        marginX="16px"
        marginBottom="16px"
        paddingX="16px"
        borderRadius="8px"
        shadow={4}
        backgroundColor={color.palette.white}
        {...restProps}
      >
        <View flexDirection="row" alignItems="center">
          <AutoImage source={img} style={IMAGE} />
          <View flex={1} paddingX="8px" marginY="6px">
            <Button testID="jala:id/offline-help-button" preset="link" onPress={() => setCollapse(!collapse)}
                    colorScheme={colorScheme}>
              <View flexDirection="row" alignItems="center" marginBottom="4px">
                <Text preset="title4" mr="4px" tx="offlineModeWeakConnection" colorScheme={colorScheme} />
                <JalaIcon name="help" size={14} color={color.palette.gray600} />
              </View>
            </Button>
            <Text preset="body3" colorScheme={colorScheme}>
              <Text preset="body3" tx="offline" colorScheme={colorScheme} />
              <Text preset="title5" text={modelsCount} colorScheme={colorScheme} />
            </Text>
          </View>
          <Button
            testID="jala:id/push-button"
            text={buttonText}
            preset="link"
            textPreset="title3"
            disabled={isSuccess || isLoading}
            textStyle={BTN_TEXT(progress, errors, isSuccess)}
            onPress={pushAll}
          />
        </View>
        {!collapse && <Text preset="body3" marginBottom={2} tx="offlineModeContent" colorScheme={colorScheme} />}
      </View>
    </View>
  )
}))
